/**
 * "Reject" — a Resource Manager or Admin sends a plan that is In Review or Staffing back to
 * its author, with a reason (workspace feat-staffing-plan-status-workflow, 2026-10-28).
 *
 * The plan's Resourcing status becomes Rejected and its sales status is untouched. The
 * server raises the rejection as a Blocking Staffing Concern (RP-34), so the reason gets a
 * thread on the Activity tab, the author and the Account's owners are told, and it shows on
 * the Changes tab even while the plan is Open. Raise Concern's "Blocking" option is the same
 * rejection; this is the findable way in.
 */

import { Ban } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useRejectResourcePlan } from "@/hooks/use-resource-plans";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

/** The server's limit on a rejection reason (rejectPlanSchema). */
const MAX_REASON = 1000;

export function RejectPlanButton({ plan }: { plan: ResourcePlanDetailView }) {
  const [open, setOpen] = useState(false);
  if (plan.canReject !== true) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Ban className="h-3.5 w-3.5" />
        Reject
      </Button>
      {open && <RejectPlanDialog plan={plan} onClose={() => setOpen(false)} />}
    </>
  );
}

function RejectPlanDialog({
  plan,
  onClose,
}: {
  plan: ResourcePlanDetailView;
  onClose: () => void;
}) {
  const reject = useRejectResourcePlan(plan.id);
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Reject this plan</DialogTitle>
          <DialogDescription>
            {`Sends "${plan.name}" back to ${plan.ownerName ?? "its author"} with your reason. Resourcing becomes Rejected; the plan's own status doesn't change. They resubmit it once they've made the changes.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="reject-reason">Why can't it be staffed as it is?</Label>
          <Textarea
            id="reject-reason"
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={MAX_REASON}
            aria-describedby="reject-reason-hint"
          />
          <p id="reject-reason-hint" className="text-xs text-muted-foreground">
            Required. The author reads this on the plan and on its Changes tab.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={reject.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={trimmed === "" || reject.isPending}
            onClick={() => reject.mutate(trimmed, { onSuccess: onClose })}
          >
            {reject.isPending ? "Rejecting…" : "Reject plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
